import styled from '@emotion/styled';

import { Container } from '@gumtree/ui-library';
import {
    fontSizes,
    colors,
    mediaQuery,
    breakpoints,
    gutterSizes,
    fontWeights,
    votColorVariables,
} from '@gumtree/ui-library/src/base/theme';

export const BrowseLinksContainer = styled(Container)`
    clear: both;

    &.browse-links-container--rh-mobile-single-location {
        .tabs:before,
        .tabs .tabs-title:before {
            content: none;
            display: none;
        }
    }

    h2 {
        font-size: ${fontSizes.base};
    }

    .tabs {
        background: ${colors.white};

        &:empty:before {
            display: none;
        }

        .tabs-title {
            background: ${colors.white};

            ${mediaQuery.until(breakpoints.small)} {
                padding-bottom: ${gutterSizes.xlarge};
            }

            &:before {
                background: ${colors.white};
            }
        }

        #top-locations-tabpanel {
            text-align: center;
        }

        .tabs-content {
            background: ${colors.white};

            h2 {
                margin: 0 0 ${gutterSizes.medium} ${gutterSizes.base};
            }

            h4 {
                margin: ${gutterSizes.medium} 0;
            }

            .top-searches-content {
                ${mediaQuery.from(breakpoints.medium)} {
                    grid-template-columns: repeat(7, minmax(0, 1fr));
                }

                .section {
                    padding: 0 ${gutterSizes.base};
                }
            }
        }
    }

    .link-list {
        width: 83.3%;
        margin: 0 auto;

        .item {
            margin: 0 5px 5px 0;

            :after {
                padding-left: 10px;
                font-weight: ${fontWeights.bolder};
                color: ${colors.bark20};
            }

            .link {
                padding: 0;
                font-size: ${fontSizes.base};
                font-weight: ${fontWeights.lightBold};
            }
        }
    }
`;

export const DesktopBrowseLinksContainer = styled(Container)`
    &.container {
        clear: both;
        padding-bottom: ${gutterSizes.xlarge};
        padding-top: ${gutterSizes.xlarge};
    }

    .desktop-browse-links__tabs {
        align-items: flex-start;
        display: flex;
        gap: ${gutterSizes.xlarge};
    }

    .desktop-browse-links__tab {
        align-items: center;
        background: none;
        border: 0;
        color: ${votColorVariables.textMuted};
        cursor: pointer;
        display: flex;
        flex-direction: column;
        font-family: inherit;
        font-size: ${fontSizes.medium};
        font-weight: ${fontWeights.bold};
        gap: ${gutterSizes.small};
        line-height: 24px;
        padding: 0;

        &:hover {
            color: ${votColorVariables.textSecondary};
        }

        &[aria-selected='false'] {
            color: ${votColorVariables.textMuted};
        }

        &[aria-selected='true'] {
            color: ${votColorVariables.textPrimary};
        }
    }

    .desktop-browse-links__marker {
        background: ${votColorVariables.accent};
        border-radius: 4px;
        display: block;
        height: 4px;
        width: 24px;
    }

    .desktop-browse-links__single-title {
        color: ${votColorVariables.textPrimary};
        font-size: ${fontSizes.medium};
        font-weight: ${fontWeights.bold};
        line-height: 24px;
        margin: 0;
    }

    .desktop-browse-links__tabpanel {
        margin-top: ${gutterSizes.large};
        width: 100%;
    }
`;

export const VipBrowseLinksContainer = styled(Container)`
    --vip-vot-browse-links-font-size: 14px;
    --vip-vot-browse-links-line-height: 22px;

    &.container {
        clear: both;
        &.section {
            padding: 32px 12px;
        }
    }

    .vip-vot-browse-links__tabs {
        border-bottom: 1px solid ${votColorVariables.borderSubtle};
        display: flex;
        justify-content: center;
        margin: 0 auto;
        width: 229px;
    }

    .vip-vot-browse-links__tab {
        align-items: center;
        background: none;
        border: 0;
        color: ${votColorVariables.textDisabled};
        cursor: pointer;
        display: flex;
        flex-direction: column;
        font-family: inherit;
        font-size: 16px;
        font-weight: ${fontWeights.normal};
        gap: 4px;
        height: 34px;
        justify-content: center;
        line-height: 24px;
        padding: 4px 12px 0;
        white-space: nowrap;
        outline: none;

        &:hover {
            color: ${votColorVariables.textSecondary};
        }

        &[aria-selected='true'] {
            color: ${votColorVariables.textPrimary};
        }
    }

    .vip-vot-browse-links__marker {
        background: ${votColorVariables.accent};
        border-radius: 2px;
        display: block;
        height: 2px;
        width: 16px;
    }

    .vip-vot-browse-links__tab[aria-selected='false'] .vip-vot-browse-links__marker {
        visibility: hidden;
    }

    .vip-vot-browse-links__tabpanel {
        margin-top: 20px;
        width: 100%;
    }

    ${mediaQuery.from(breakpoints.medium)} {
        &.container.container.section {
            box-sizing: border-box;
            max-width: none;
            padding-right: ${gutterSizes.xlarge};
            padding-left: ${gutterSizes.xlarge};
            width: 100%;
        }

        --vip-vot-browse-links-font-size: 16px;
        --vip-vot-browse-links-line-height: 24px;

        &[data-vot-tab-count='1'] {
            --vip-vot-browse-links-font-size: 14px;
            --vip-vot-browse-links-line-height: 22px;
        }

        .vip-vot-browse-links__tabs {
            width: 345px;
        }

        .vip-vot-browse-links__tab {
            box-sizing: border-box;
            font-size: 20px;
            font-weight: ${fontWeights.bold};
            gap: 0;
            height: 32px;
            line-height: 30px;
            padding-bottom: 0;
            padding-top: 0;
        }

        .vip-vot-browse-links__tabpanel {
            margin-top: 16px;
        }
    }

    ${mediaQuery.from(breakpoints.xlarge)} {
        &.container.container.section {
            max-width: 1440px;
            padding-right: 80px;
            padding-left: 80px;
        }
    }
`;
